import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Typography, Chip, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Box,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { poApi, grnApi, type Po } from '../../api/procurement.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function PoDetailDialog({ poId, onClose }: { poId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = poId !== null;
  const [error, setError] = useState<string | null>(null);
  const [receipts, setReceipts] = useState<Record<number, number>>({});
  const [transporter, setTransporter] = useState('');

  const po = useQuery({ queryKey: ['po', poId], queryFn: () => poApi.get(poId as number), enabled: open });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['po', poId] }); qc.invalidateQueries({ queryKey: ['pos'] }); };

  const statusMut = useMutation({ mutationFn: (action: string) => poApi.changeStatus(poId as number, action), onSuccess: refresh, onError: (e) => setError(extractApiError(e)) });
  const dispatchMut = useMutation({ mutationFn: () => poApi.addDispatch(poId as number, { transporter, status: 'dispatched' }), onSuccess: () => { setTransporter(''); refresh(); }, onError: (e) => setError(extractApiError(e)) });
  const grnMut = useMutation({
    mutationFn: () => grnApi.create({ poId: poId as number, items: Object.entries(receipts).filter(([, q]) => q > 0).map(([id, q]) => ({ poItemId: Number(id), receivedQty: q })) }),
    onSuccess: () => { setReceipts({}); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });

  if (!open) return null;
  const d: Po | undefined = po.data;
  const items = d?.items ?? [];
  const canReceive = d && ['approved', 'in_transit', 'partially_received'].includes(d.status);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{d?.code} <Chip size="small" label={d?.status} sx={{ ml: 1 }} /></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography variant="body2" mb={1}>Grand total: ₹{(d?.grandTotal ?? 0).toLocaleString()} · Vendor #{d?.vendorId}</Typography>

        <Stack direction="row" spacing={1} mb={2}>
          {d?.status === 'draft' && hasPermission('po.update') && <Button size="small" variant="outlined" onClick={() => statusMut.mutate('submit')}>Submit</Button>}
          {d?.status === 'pending_approval' && hasPermission('po.approve') && (
            <>
              <Button size="small" color="success" variant="outlined" onClick={() => statusMut.mutate('approve')}>Approve</Button>
              <Button size="small" color="error" variant="outlined" onClick={() => statusMut.mutate('reject')}>Reject</Button>
            </>
          )}
          {d?.status === 'received' && hasPermission('po.update') && <Button size="small" variant="outlined" onClick={() => statusMut.mutate('close')}>Close</Button>}
        </Stack>

        <Table size="small">
          <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Unit</TableCell><TableCell>Ordered</TableCell><TableCell>Received</TableCell><TableCell>Balance</TableCell>{canReceive && <TableCell>Receive Now</TableCell>}</TableRow></TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id}>
                <TableCell>{it.description}</TableCell><TableCell>{it.unit}</TableCell><TableCell>{it.quantity}</TableCell>
                <TableCell>{it.receivedQty}</TableCell><TableCell>{it.balanceQty}</TableCell>
                {canReceive && <TableCell><TextField variant="standard" type="number" value={receipts[it.id] ?? ''} onChange={(e) => setReceipts((s) => ({ ...s, [it.id]: Number(e.target.value) }))} disabled={it.balanceQty <= 0} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {canReceive && hasPermission('grn.create') && (
          <Box mt={1}><Button variant="contained" size="small" disabled={!Object.values(receipts).some((q) => q > 0)} onClick={() => grnMut.mutate()}>Record GRN (Goods Receipt)</Button></Box>
        )}

        {d && ['approved', 'in_transit', 'partially_received'].includes(d.status) && hasPermission('po.update') && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Dispatch planning</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="Transporter" value={transporter} onChange={(e) => setTransporter(e.target.value)} />
              <Button size="small" variant="outlined" onClick={() => dispatchMut.mutate()}>Add Dispatch Plan</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">Dispatch plans: {d.dispatchPlans?.length ?? 0}</Typography>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
